/**
 * PriorityQueue — "What to close this week", the Command Center's Carbon
 * DataTable (mockup 01): a pillar content switcher and a search field in the
 * toolbar, then one row per ranked action with its severity (shape, then the
 * word), the item, its pillar tag, the workload and its status.
 *
 * Data: usePriorityActions({ tenantId, maxCount, pillar }). The switcher
 * refetches with the chosen pillar, so each pillar view shows up to 6 ranked
 * actions for that pillar rather than narrowing one all-pillar fetch, which
 * could be empty for a pillar. See
 * .agents/Tasks/High Priority/operations-priority-alerts-curation.md §4.5.
 * Search narrows the loaded rows on the client.
 *
 * The endpoint caps the list and returns no total, so the copy never claims
 * "of N open" (blueprint §7 C24, D30). Workload and status come from the
 * action's `meta` segments (C22, C23; cc-queue.ts). Impact orders the rows
 * (the server ranks them); the mockup shows no impact chip or external link,
 * so a row is its title link and four short cells. The footer links the
 * chosen pillar's full list.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import type { Pillar, PriorityAction } from '@/lib/posture-brief'
import {
  ContentSwitcher,
  DataTable,
  IconArrowRight,
  TableSearch,
  Tag,
  TONE_CATEGORY,
  type DataColumn,
} from '@/components/carbon'
import PriorityPill from './PriorityPill'
import {
  parseActionMeta,
  QUEUE_FULL_LIST,
  QUEUE_PILLAR_LABEL,
  queueDescription,
  queueFooter,
  queueStatus,
  searchActions,
} from './cc-queue'

type Filter = 'all' | Pillar

const FILTERS: ReadonlyArray<{ id: Filter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'compliance', label: 'Baseline' },
  { id: 'bpa', label: 'Best Practice' },
  { id: 'identity', label: 'Identity' },
]

export interface PriorityQueueProps {
  /** Active tenant. When undefined the hook stays idle and the queue
   *  renders its empty state without firing a request. */
  tenantId: string | undefined
  /** The tenant has no assessment yet: the empty table says the queue fills after the first one, not "Nothing to review." */
  noData?: boolean
}

const COLUMNS: DataColumn<PriorityAction>[] = [
  {
    id: 'severity',
    header: 'Severity',
    nowrap: true,
    cell: a => <PriorityPill severity={a.severity} pillar={a.pillar} />,
  },
  {
    id: 'item',
    header: 'Item',
    primary: true,
    cell: a => (
      <Link to={a.href} className="[overflow-wrap:anywhere] hover:underline">
        {a.title}
      </Link>
    ),
  },
  {
    id: 'pillar',
    header: 'Pillar',
    nowrap: true,
    cell: a => (
      <Tag kind="cat" category={TONE_CATEGORY[a.pillar]}>
        {QUEUE_PILLAR_LABEL[a.pillar]}
      </Tag>
    ),
  },
  {
    id: 'workload',
    header: 'Workload',
    nowrap: true,
    cell: a => parseActionMeta(a.meta, a.pillar).workload,
  },
  {
    id: 'status',
    header: 'Status',
    nowrap: true,
    cell: a => queueStatus(a.meta, a.pillar),
  },
]

export default function PriorityQueue({ tenantId, noData = false }: PriorityQueueProps) {
  const [filter, setFilter] = useState<Filter>('all')
  const [query, setQuery] = useState('')
  const { actions, isLoading, error } = usePriorityActions({
    tenantId,
    maxCount: 6,
    pillar: filter,
  })

  // Server already returns the right slice for the chosen filter; search only narrows what loaded.
  const rows = useMemo(() => searchActions(actions, query), [actions, query])
  const fullList = QUEUE_FULL_LIST[filter]

  const empty = error
    ? 'The ranked queue did not load. Refresh the page to try again.'
    : query.trim()
      ? 'Nothing matches this search.'
      : noData
        ? 'The queue fills after the first assessment.'
        : filter === 'all'
          ? 'Nothing to review.'
          : undefined

  return (
    <DataTable<PriorityAction>
      className="mt-12"
      title="What to close this week"
      description={queueDescription(isLoading || error ? 0 : actions.length)}
      toolbar={
        <>
          <ContentSwitcher
            items={FILTERS.map(f => ({ id: f.id, label: f.label }))}
            value={filter}
            onChange={id => setFilter(id as Filter)}
            aria-label="Filter by pillar"
            className="max-c-md:basis-full"
          />
          <TableSearch value={query} onChange={setQuery} label="Search open items" />
        </>
      }
      columns={COLUMNS}
      rows={error ? [] : rows}
      rowId={a => a.id}
      rowLabel={a => a.title}
      isLoading={isLoading}
      loadingRows={6}
      empty={empty}
      footer={
        !isLoading && !error && actions.length > 0 ? (
          <>
            <span className="text-helper text-text-helper">{queueFooter(rows.length, actions.length)}</span>
            {fullList ? (
              <Link to={fullList.href} className="inline-flex items-center gap-2 text-body text-link hover:text-link-hover hover:underline">
                {fullList.label}
                <IconArrowRight />
              </Link>
            ) : null}
          </>
        ) : undefined
      }
    />
  )
}
